import {useCallback, useEffect, useRef, useState} from 'react';
import {toast} from 'react-toastify';

import {businessAffiliateService} from '@/services/businessAffiliateService';
import type {
  AdminBusinessReferral,
  AdminPaginatedEnvelope,
  AffiliateReferralListParams,
} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';

import type {AffiliateReferralsProps} from './types';

const PAGE_SIZE = 10;

const useAffiliateReferrals = (): AffiliateReferralsProps => {
  const [referrals, setReferrals] = useState<AdminBusinessReferral[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<unknown>(null);
  const [itemTotal, setItemTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const currentPageRef = useRef<number>(0);
  const [displayPage, setDisplayPage] = useState<number>(1);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);
  const [appliedFilters, setAppliedFilters] =
    useState<AffiliateReferralListParams>({});
  const [filtersToApply, setFiltersToApply] =
    useState<AffiliateReferralListParams>({});
  const [filterCount, setFilterCount] = useState<number>(0);

  const applyPage = useCallback(
    (
      requestedPage: number,
      response: AdminPaginatedEnvelope<AdminBusinessReferral[]>,
    ) => {
      currentPageRef.current = requestedPage;
      setDisplayPage(requestedPage + 1);
      setPage(requestedPage + 1);
      setReferrals(response.data ?? []);
      setItemTotal(response.total ?? 0);
      setError(null);
      setIsLoading(false);
    },
    [],
  );

  const failLoad = useCallback((e: unknown) => {
    setError(e);
    setIsLoading(false);
  }, []);

  const fetchReferrals = useCallback(
    async (params: AffiliateReferralListParams) => {
      const requestedPage = params.page ?? 0;
      try {
        applyPage(
          requestedPage,
          await businessAffiliateService.getReferrals({
            ...appliedFilters,
            page: requestedPage,
            limit: PAGE_SIZE,
          }),
        );
      } catch (e) {
        failLoad(e);
      }
    },
    [appliedFilters, applyPage, failLoad],
  );

  const fetchList = useCallback(
    (params: AffiliateReferralListParams) => {
      setIsLoading(true);
      fetchReferrals(params).catch(
        logRejection('Failed to load affiliate referrals'),
      );
    },
    [fetchReferrals],
  );

  useEffect(() => {
    businessAffiliateService
      .getReferrals({...appliedFilters, page: 0, limit: PAGE_SIZE})
      .then((response) => applyPage(0, response))
      .catch((e: unknown) => {
        // These filters never loaded, so the rows on screen belong to other
        // filters: drop them rather than show them as merely out of date.
        setReferrals([]);
        setItemTotal(0);
        failLoad(e);
      });
  }, [appliedFilters, applyPage, failLoad]);

  const disqualify = useCallback(
    async (referralId: string, reason: string): Promise<boolean> => {
      try {
        await businessAffiliateService.disqualifyReferral(referralId, {reason});
        await fetchReferrals({page: currentPageRef.current});
        toast.success('Referral disqualified');
        return true;
      } catch (e) {
        toast.error(getErrorMessage(e, 'Could not disqualify this referral'));
        return false;
      }
    },
    [fetchReferrals],
  );

  const selectAppliedFilters = useCallback(
    (name: string, value: string | boolean | undefined) => {
      setFiltersToApply((prev) => {
        const updated = {...prev, [name]: value};
        setFilterCount(
          Object.values(updated).filter(
            (val) => val !== undefined && val !== '' && val !== false,
          ).length,
        );
        return updated;
      });
    },
    [],
  );

  const handleApplyFilters = useCallback(() => {
    setIsLoading(true);
    setAppliedFilters(filtersToApply);
    setIsPageReset((prev) => !prev);
  }, [filtersToApply]);

  const clearFilters = useCallback(() => {
    setIsLoading(true);
    setAppliedFilters({});
    setFiltersToApply({});
    setFilterCount(0);
    setIsPageReset((prev) => !prev);
  }, []);

  return {
    referrals,
    isLoading,
    error,
    pageInfo: {total: itemTotal, page},
    displayPage,
    isPageReset,
    filterCount,
    filtersToApply,
    fetchList,
    disqualify,
    clearFilters,
    selectAppliedFilters,
    handleApplyFilters,
  };
};

export default useAffiliateReferrals;
